import {
  AlertTriangle,
  CircleDollarSign,
  Flame,
  Gauge,
  Layers3,
  Scissors,
  ShieldCheck,
  Sparkles,
  Timer,
} from 'lucide-react';
import { displayUnitValue } from '../../../units/unitConversions';

const materialDetailInsights = {
  mechanicalProperty: {
    title: 'Mechanical strength / hardness',
    short: 'No authoritative mechanical strength or hardness value is available for this material.',
    sections: [],
  },
  hardness: {
    title: 'Hardness',
    short: 'Hardness describes the material in the scale used by its authoritative source or material condition.',
    sections: [
      {
        title: 'Reading this value',
        paragraphs: [
          'The displayed scale is the authoritative stored property. Approximate equivalents belong in the separate hardness comparison tool.',
          'Material condition and microstructure can affect how hardness relates to strength and machining behavior.',
        ],
      },
    ],
  },
  tensileStrength: {
    title: 'Tensile Strength',
    short:
      'Tensile strength helps show whether tool choice can move toward a sharper, tougher edge or needs a harder, stronger and more protected edge.',
    sections: [
      {
        title: 'Tool edge balance',
        paragraphs: [
          'Higher-strength material generally creates higher mechanical load on the tool and cutting edge.',
          'Lower-strength material often allows a sharper and more positive cutting geometry. Higher-strength material usually pushes us toward a stronger, better-protected edge.',
          'This is not a rule by itself. Tensile strength should be read together with elongation, hardness, composition and the cutting conditions.',
        ],
      },
      {
        title: 'Four practical extremes',
        cards: [
          {
            title: 'Sharp + hard edge',
            text:
              'Can cut very well, but a very sharp edge made from a hard, wear-resistant cutting material has less edge support and may chip under high mechanical load.',
          },
          {
            title: 'Protected + hard edge',
            text:
              'Useful for stronger materials and many harder workpieces, but in soft ductile materials it may create excessive force, adhesion or poor chip formation.',
          },
          {
            title: 'Sharp + tough edge',
            text:
              'Often useful for soft ductile materials, but higher mechanical load or abrasive conditions may make it wear rapidly.',
          },
          {
            title: 'Protected + tough edge',
            text:
              'Mechanically robust, but it may be too blunt for soft ductile materials and not wear resistant enough for very hard materials.',
          },
        ],
      },
      {
        title: 'Choosing priorities',
        paragraphs: [
          'There is no single best tool for a material. Tool geometry, tool material and edge preparation can be adjusted depending on what we want to achieve.',
          'The strength of the work material creates the basic boundaries within which we can make those choices. The tool still needs to be strong enough for the mechanical load created by the material, while remaining sharp and tough enough for the machining strategy we want to use.',
        ],
      },
      {
        title: 'Chip formation orientation',
        paragraphs: [
          'For many steels, increasing strength generally makes chip breaking easier. Very soft steels can produce long, difficult chips.',
          (unitSystem) => `Around ${displayUnitValue(500, 'mpa', unitSystem)} and above, many common carbon steels already tend to produce more manageable chips. Treat this only as practical orientation, not a universal threshold.`,
        ],
      },
    ],
    relationships: [
      {
        label: 'Lower strength',
        result: 'sharper cutting edge',
        Icon: Scissors,
      },
      {
        label: 'Higher strength',
        result: 'stronger, more protected edge',
        Icon: ShieldCheck,
      },
    ],
  },
  thermalConductivity: {
    title: 'Thermal Conductivity',
    short:
      'Thermal conductivity helps us understand how heat is shared between the workpiece and the cutting tool, giving an approximate clue about the cutting speed the tool can sustain.',
    sections: [
      {
        title: 'Heat distribution: workpiece and tool',
        paragraphs: [
          'Cutting generates heat in the cutting zone. For practical tool selection, we compare how the workpiece material and the cutting tool handle that heat.',
          'This comparison gives us an approximate picture of how strongly the tool may be thermally loaded for a particular machining strategy. It is a practical indicator, not a direct formula for maximum cutting speed.',
          'If the workpiece conducts heat poorly relative to the cutting system, more thermal load may remain concentrated around the cutting zone and tool. This generally gives us less room to increase cutting speed.',
          'If heat can be managed more effectively, the process may allow a higher cutting speed before tool temperature becomes the limiting factor.',
        ],
      },
      {
        title: 'Every tool has a usable Vc range',
        paragraphs: [
          'Every cutting tool has a practical usable cutting-speed range. At the lower end, the cutting speed must still be high enough for the edge geometry, edge preparation and chip formation to work as intended.',
          'If cutting speed is too low for the tool and operation, the edge may rub instead of cut cleanly. Friction, built-up edge and poor chip formation can become the problem.',
          'At the upper end, thermal load becomes too high for the tool to keep reasonable tool life. This upper limit is not one universal number. It depends on the workpiece, tool, geometry, coating, engagement, cooling conditions and machining strategy.',
        ],
        cards: [
          {
            title: 'Minimum practical Vc',
            text:
              'The edge still needs enough speed to cut as designed instead of rubbing or forming poor chips.',
          },
          {
            title: 'Usable Vc range',
            text:
              'Inside this range, the machinist can choose conditions according to productivity, tool life and process reliability.',
          },
          {
            title: 'Maximum practical Vc',
            text:
              'Thermal load becomes too high and tool wear may accelerate quickly.',
          },
        ],
      },
      {
        title: 'Productivity and tool life',
        paragraphs: [
          'The best cutting speed does not always mean the longest possible tool life. Inside the usable Vc range, the machinist can move the process toward different priorities.',
          'Lower Vc usually means more conservative thermal loading, potentially longer tool life and lower productivity. Higher Vc can raise productivity, but it also increases thermal load and may shorten tool life.',
          'Sometimes it is better to choose a cutting speed so one cutting edge reliably finishes one complete workpiece and is then replaced. Tool life can be intentionally matched to the workpiece cycle, predictable tool changes, process reliability and productivity requirements.',
        ],
      },
      {
        title: 'Hot pan analogy',
        paragraphs: [
          'Think of touching a hot pan. Whether you burn yourself depends not only on how hot the pan is, but also on how effectively heat reaches you and how long you stay in contact with it.',
          'Touch it very briefly and your skin may tolerate it. Stay in contact longer and enough heat transfers to cause a burn.',
          'A cutting tool faces a similar problem. It is affected by thermal intensity and by exposure time. In machining, that leads directly to cutting speed and engagement time.',
        ],
      },
      {
        title: 'Cutting speed and engagement time',
        paragraphs: [
          'Higher cutting speed generally increases thermal load in the cutting zone. Poor workpiece thermal conductivity combined with high Vc gives a greater risk that tool temperature becomes the limiting factor.',
          'This is why materials with poor thermal conductivity often require lower cutting speeds than materials that manage heat more effectively, even when their mechanical strength appears similar. Vc is still not determined by thermal conductivity alone.',
          'Temperature is not the only issue. The time the cutting edge remains exposed to the thermal load also matters.',
          'In turning and drilling, the cutting edge can remain continuously engaged. The duration and length of the cut matter because the edge has little or no cooling period while it remains in the cut.',
          'Milling is interrupted cutting. The edge spends part of each revolution inside the material and part outside the material. Time outside the cut gives the edge an opportunity to cool.',
          'Radial engagement, or width of cut ae, affects thermal loading. Higher radial engagement generally keeps the edge in the material for a larger part of each revolution. Lower radial engagement gives it more time outside the cut.',
          'The practical question is not only “how hot?” It is also “for how long?”',
        ],
      },
      {
        title: 'Tool temperature limit',
        paragraphs: [
          'Every cutting-tool material has a temperature range in which it can retain useful cutting properties.',
          'HSS and PM-HSS lose useful hardness when their temperature becomes too high. Their exact thermal capability depends on the specific grade and composition.',
          'Cemented carbide can operate at much higher cutting temperatures, but excessive thermal load still accelerates wear and can eventually destroy the cutting edge.',
          'Once the useful thermal range of the cutting tool is exceeded, tool wear can accelerate very quickly. This is the practical upper boundary of the usable Vc range.',
        ],
      },
      {
        title: 'Coating',
        paragraphs: [
          'A suitable coating can significantly change how much thermal and wear load the cutting tool can tolerate.',
          'Depending on the application, a coating can provide thermal protection, reduce friction, improve wear resistance and protect the cutting tool.',
          'A suitable coated tool may allow a significantly higher usable cutting speed than an equivalent uncoated tool while still maintaining acceptable tool life. This depends on the coating, tool, material and operation.',
        ],
      },
      {
        title: 'How ASiJoLab uses cutting speed',
        paragraphs: [
          'The cutting-speed ranges presented by ASiJoLab are intended as practical working ranges, not theoretical physical limits.',
          'When an appropriate cutting tool is selected for a specific material, the recommended range is designed so that the tool has a realistic chance to machine the material reliably.',
          'These ranges come from long-term practical experience in machining, cutting-tool testing and cutting-tool development.',
          'The range gives the machinist room to optimize the process. Within the recommended range, conditions can be chosen according to productivity, tool life, process reliability, workpiece cycle and predictable tool changes.',
          'ASiJoLab values should be used as practical starting and working ranges based on experience, not as universal guaranteed limits.',
        ],
      },
    ],
    relationships: [
      {
        label: 'Workpiece + tool',
        result: 'heat distribution',
        Icon: Flame,
      },
      {
        label: 'Vc + contact time',
        result: 'usable speed range and tool life',
        Icon: Timer,
      },
    ],
  },
  elongation: {
    title: 'Elongation',
    short:
      'Elongation complements tensile strength and helps predict how the material will deform and form chips before it breaks.',
    sections: [
      {
        title: 'Read it with tensile strength',
        paragraphs: [
          'Tensile strength alone does not tell the whole story. Two materials with similar tensile strength can behave very differently if their elongation is different.',
          'Tensile strength tells us one part of the mechanical load. Elongation tells us much more about how the material may deform and form chips.',
        ],
      },
      {
        title: 'High and low elongation',
        cards: [
          {
            title: 'High elongation',
            text:
              'The material can deform considerably before fracture. Chip control becomes more important and sharper geometry with an appropriate chipbreaker may help.',
          },
          {
            title: 'Low elongation',
            text:
              'The material fractures with little deformation. It may produce shorter chips and can sometimes allow a stronger cutting edge than tensile strength alone would suggest.',
          },
        ],
      },
      {
        title: 'Practical example',
        paragraphs: [
          (unitSystem) => `A relatively soft material around ${displayUnitValue(300, 'mpa', unitSystem)}–${displayUnitValue(400, 'mpa', unitSystem)} might look difficult because of its low strength. If its elongation is only around 3-6%, it may still produce short, easily broken chips.`,
          'Cast iron is an intuitive example: it can have respectable tensile strength, but very low elongation makes its chip behavior completely different from ductile steel of similar strength.',
        ],
      },
    ],
  },
  chemicalComposition: {
    title: 'Chemical Composition',
    short:
      'Chemical composition can reveal machining behavior that tensile strength, hardness or a material-group label may hide.',
    sections: [
      {
        title: 'What chemical composition tells a machinist',
        paragraphs: [
          'Chemical composition should not be read as a dictionary of individual elements. A machinist uses it to look for combinations and warning signs.',
          'The useful question is: what material behavior should I expect, what machining risk does that create, and what should I watch for at the machine?',
          'Composition can help explain hardening potential, abrasive wear, carbide phases, work-hardening tendency, behavior after thermal cutting or welding, and why two apparently similar materials may machine differently.',
          'A material group label is not a tool-selection rule. “Stainless steel”, “tool steel” and “nickel alloy” are classifications, not machining instructions.',
          'Composition must be considered together with tensile strength, hardness, elongation, thermal conductivity, microstructure, material condition and manufacturing route.',
        ],
      },
      {
        title: 'Start with carbon',
        paragraphs: [
          'For steels, carbon is one of the fastest first orientation points. These ranges are practical machining orientation zones, not exact universal boundaries.',
          'Carbon should start the investigation, not finish it. The next question is always what the rest of the alloy system and delivered material condition do to the machining behavior.',
        ],
        cards: [
          {
            title: 'Very low carbon: around 0.05% C',
            text:
              'Very limited potential hardness from carbon itself. These materials are often relatively soft, ductile and readily weldable, but low carbon does not automatically mean easy machining. Ask what else is in the alloy.',
          },
          {
            title: 'Low carbon: around 0.20% C',
            text:
              'Many common structural steels fall near this area. They generally have limited carbon hardening potential and often machine as relatively soft, ductile steels compared with higher-carbon steels.',
          },
          {
            title: 'About 0.40% C and above',
            text:
              'This deserves more attention. Carbon provides enough potential hardness that heat treatment or locally rapid cooling can produce much harder structures, provided the steel has sufficient hardenability and the thermal conditions allow it.',
          },
          {
            title: 'High carbon: around 0.8% C and above',
            text:
              'This is not a universal carbide threshold. At high carbon levels, stop looking at carbon alone and inspect carbide-forming elements such as Cr, V, W and Mo.',
          },
        ],
      },
      {
        title: 'Thermal-cut low-carbon steel is a different problem',
        paragraphs: [
          'If a low-carbon structural steel has been laser cut, plasma cut or flame cut, the first machining pass can still be extremely unpleasant.',
          'The surface may contain scale, slag, rough irregular areas and interrupted contact. This can create a very aggressive interrupted cut and may require a tough, robust cutting edge.',
          'Rough or interrupted cutting is not the same thing as hardened material. Separating these problems matters because they can lead to different tool choices.',
        ],
      },
      {
        title: 'Carbon content is not hardenability',
        paragraphs: [
          'Carbon content is not the same as hardenability. Carbon strongly affects potential hardness, while hardenability also depends on alloying, cooling conditions, section size and material structure.',
          'Around 0.40% C and above, surfaces affected by laser cutting, plasma cutting, flame cutting, welding, induction hardening or other local thermal processes deserve more attention.',
          'The bulk material may appear manageable while a locally transformed surface can be much harder and more damaging to the cutting edge.',
        ],
      },
      {
        title: 'Carbon and carbide formers: abrasion warning',
        paragraphs: [
          'As carbon content rises, abrasive wear generally becomes a greater concern, especially when carbon is combined with strong carbide-forming elements and the right microstructure.',
          'The important machining question becomes: does this composition suggest a significant population of hard carbide phases?',
          'Bulk tensile strength or bulk hardness may not reveal the whole machining problem. The cutting edge interacts with the actual microstructure, and very hard carbide phases can cause severe abrasive wear even when the overall hardness does not look extreme.',
        ],
        cards: [
          {
            title: 'Higher carbon',
            text:
              'Start paying closer attention to abrasive wear risk, but do not treat carbon alone as the full answer.',
          },
          {
            title: 'Strong carbide formers',
            text:
              'Elements such as Cr, V, W and Mo can support hard carbide phases when combined with carbon and the right structure.',
          },
          {
            title: 'Machining warning',
            text:
              'Hard carbides can increase abrasive flank wear and may require more wear-resistant tooling than strength alone suggests.',
          },
        ],
      },
      {
        title: 'Real example: D2 / 1.2379',
        paragraphs: [
          'An annealed D2 / 1.2379 tool steel can appear reasonably machinable if we look only at bulk hardness or tensile strength.',
          'Its high carbon and chromium content can produce a significant carbide population. The cutting tool encounters hard abrasive phases inside a comparatively softer matrix.',
          'Abrasive flank wear can become important, so wear-resistant cutting material, suitable coating and sensible Vc may matter more than tensile strength alone suggests.',
          'Bulk hardness does not always tell the whole machining story. The composition explains why.',
        ],
      },
      {
        title: 'Low carbon? Look at the rest of the alloy',
        paragraphs: [
          'Low carbon does not automatically mean easy machining. If carbon is around 0.20% or lower, inspect the rest of the alloy system.',
          'A low-carbon structural steel and a low-carbon austenitic stainless steel may both contain little carbon, but they can behave completely differently during machining.',
          'For an austenitic stainless steel, the complete alloy system and resulting structure can produce high ductility, strong work-hardening tendency, difficult chip control and relatively poor thermal conductivity.',
          'Nickel-base superalloys are an extreme example. Nickel is the base material rather than iron, and these alloys can combine very strong work hardening with difficult thermal behavior.',
          'Do not blame one element alone. Composition, structure and properties must be interpreted together.',
        ],
      },
      {
        title: 'Work hardening: tool sharpness is a balance',
        paragraphs: [
          'Work-hardening material does not simply mean “use the sharpest possible tool”. A sufficiently sharp cutting edge is useful because it cuts rather than unnecessarily ploughing and deforming the material.',
          'Less unnecessary deformation can reduce additional work hardening and cutting forces. But the tool must also survive contact with the work-hardened layer.',
          'An excessively weak or delicate cutting edge can chip or break when it encounters harder work-hardened material.',
        ],
        cards: [
          {
            title: 'Sharp enough',
            text:
              'Use enough sharpness to cut cleanly instead of unnecessarily deforming the material.',
          },
          {
            title: 'Strong enough',
            text:
              'Keep enough edge strength to survive the hardened layer created during machining.',
          },
        ],
      },
      {
        title: 'Work hardening: avoid unnecessary interruptions',
        paragraphs: [
          'In continuous operations such as turning and drilling, once the tool is cutting correctly, unnecessary interruptions should generally be avoided in strongly work-hardening materials.',
          'Repeatedly leaving and re-entering the cut can force the edge to encounter material that has already been plastically deformed and work hardened. This can increase cutting-edge load and the risk of damage.',
          'Good cooling is important where the process and tool allow it.',
          'If the machining strategy can remain continuous, avoid unnecessary interruptions. If the process is inherently interrupted, the tool, grade, geometry and cutting conditions must be selected to tolerate that interrupted engagement.',
        ],
      },
      {
        title: 'Same grade does not guarantee same machinability',
        paragraphs: [
          'Two batches can both legitimately be sold as 1.4301 and still behave differently during machining.',
          'The material designation and chemical limits do not guarantee identical manufacturing history, cold work, previous deformation, material condition, microstructure or residual stresses.',
          'A heavily cold-worked or poorly controlled batch can arrive already significantly work hardened. Cutting conditions that worked reliably on another batch of the same nominal material may suddenly perform poorly.',
          'Supplier quality, processing route and actual delivered material condition matter more than price itself.',
        ],
        cards: [
          {
            title: 'Same material designation',
            text:
              'The name can be correct while the delivered condition and machining behavior are different.',
          },
          {
            title: 'Recommended conditions',
            text:
              'Use them as practical starting points, not absolute guarantees.',
          },
        ],
      },
      {
        title: 'Pay attention to powder metallurgy steels',
        paragraphs: [
          'Powder metallurgy steels can appear deceptively similar to conventionally produced steels if we compare only tensile strength, hardness or broad material family.',
          'Their manufacturing route and resulting microstructure can make them significantly more demanding to machine, depending on grade and microstructure.',
          'PM processing can produce a fine and homogeneous distribution of hard phases or carbides. This can create greater abrasive loading of the cutting edge than a conventionally produced material with apparently similar bulk mechanical properties.',
          'Similar mechanical properties do not guarantee similar machinability. Manufacturing route matters too.',
        ],
      },
      {
        title: 'How to read a steel composition',
        paragraphs: [
          'Step 1: start with carbon. Very low carbon means limited carbon hardening potential, but do not stop there. Around 0.20% C or lower often points toward structural-steel territory, while thermal-cut surfaces can still create rough interrupted machining even without deep hardening.',
          'Around 0.40% C and above, pay attention to hardening potential, alloying and hardenability. Inspect laser, plasma, flame-cut surfaces and heat-affected zones carefully.',
          'Around 0.8% C and above, carbon alone is no longer enough. Inspect Cr, V, W, Mo and other carbide-forming elements, and consider carbide-related abrasive wear.',
          'Step 2: if carbon is low, ask what else is in the alloy. A high Cr/Ni alloy system means you should investigate structure, ductility, thermal conductivity and work-hardening tendency.',
          'Step 3: look for carbide-forming combinations. High carbon plus Cr, V, W or Mo is an abrasive wear warning.',
          'Step 4: check how the material was produced and delivered: conventional or powder metallurgy, annealed or hardened, cold worked, thermal-cut surface, and actual batch condition.',
          'Step 5: cross-check composition against tensile strength, hardness, elongation and thermal conductivity. Composition should help explain the behavior suggested by those properties. If it does not, that discrepancy is worth investigating.',
        ],
      },
      {
        title: 'Final mental model',
        paragraphs: [
          'Do not ask only: what does each element do?',
          'Ask: what does this combination tell me about the material I am about to cut?',
          'Chemical composition points us toward carbon first, then the alloy system, carbide formers, structure and material condition. From there we can estimate hardening, work hardening, abrasion and expected machining behavior.',
          'The practical result is better tool, geometry, Vc and process-strategy decisions.',
        ],
      },
    ],
    relationships: [
      {
        label: 'Low carbon',
        result: 'look at the rest of the alloy',
        Icon: AlertTriangle,
      },
      {
        label: 'High carbon + carbide formers',
        result: 'abrasive wear risk',
        Icon: Layers3,
      },
    ],
  },
};

export const machiningPriorityItems = [
  {
    label: 'Tool life',
    Icon: Timer,
  },
  {
    label: 'Chip control',
    Icon: Scissors,
  },
  {
    label: 'Machining speed',
    Icon: Gauge,
  },
  {
    label: 'Surface quality',
    Icon: Sparkles,
  },
  {
    label: 'Process reliability',
    Icon: ShieldCheck,
  },
  {
    label: 'Tool cost',
    Icon: CircleDollarSign,
  },
];

export default materialDetailInsights;
